import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import { ReactElement } from 'react';
import { Button } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { Selection } from 'react-stately';
import { useToggle } from 'usehooks-ts';
import { Menu, MenuItem } from '@provider-directory/components/menu/menu';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import {
  languages as languageOptions,
  useLanguage,
} from '@provider-directory/hooks/useLanguage';

const LanguageButton = ({
  selectedLabel,
  className,
}: {
  selectedLabel: string;
  className?: string;
}) => {
  return (
    <Button
      id={'language-selector-button'}
      className={clsx(
        'flex items-center justify-center gap-[10px] uppercase',
        className,
      )}
      data-testid={'language-selector-btn'}
      aria-haspopup={true}
    >
      <span className={'text-body-medium-semibold'}>{selectedLabel}</span>
      <MaterialSymbol name={'keyboard_arrow_down'} />
    </Button>
  );
};

export default function LanguageSelector({
  buttonClassName = 'text-black',
}: {
  buttonClassName?: string;
}): ReactElement | null {
  const { t } = useTranslation();
  const { language, setLocale } = useLanguage();
  const { featureFlags } = useFeatureFlags();
  const [isOpen, onOpenChange] = useToggle(false);

  const onChangeLanguage = (keys: Selection) => {
    const selectedLocale =
      languageOptions.find((o) => o.value === [...keys][0]) ??
      languageOptions[0];
    setLocale(selectedLocale);
  };

  if (!featureFlags.isLocalizationEnabled) {
    return null;
  }

  return (
    <Menu
      data-testid={'localization-selector-menu'}
      TriggerComponent={LanguageButton}
      triggerComponentProps={{
        selectedLabel: language.value,
        className: buttonClassName,
      }}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      selectionMode={'single'}
      selectedKeys={new Set([language.value])}
      onSelectionChange={onChangeLanguage}
      heading={t('selectLanguage')}
      items={languageOptions}
      testIdPrefix={'language'}
      aria-label={'Select language'}
    >
      {languageOptions.map((item) => (
        <MenuItem
          id={item.value}
          key={item.value}
          data-testid={
            item.value
              ? `language-option-${item.value
                  .replace(/\s+/g, '')
                  .toLowerCase()}`
              : null
          }
        >
          {t(item.label)}
        </MenuItem>
      ))}
    </Menu>
  );
}
